import React, {
	useState,
	ReactNode,
	useCallback,
	useRef,
	useEffect
} from 'react';
import PagingContext, {
	PagingOptions,
	PagingContextItem,
	defaultPagingOptions
} from '../PagingContext';

interface PagingProviderProps {
	children: ReactNode;
	initialContexts?: Record<string, Partial<PagingOptions>>;
}

export const PagingProvider: React.FC<PagingProviderProps> = ({
	children,
	initialContexts = {}
}) => {
	/* 
	Stato per memorizzare tutti i contesti di paginazione.
	Mappa gli ID di contesto a PagingContextType specifici
	*/
	const [pagingItems, setPagingItems] = useState<
		Record<string, PagingContextItem>
	>(() => {
		const initialItems: Record<string, PagingContextItem> = {};

		// Inizializza i contesti forniti
		Object.entries(initialContexts).forEach(([contextId, options]) => {
			initialItems[contextId] = {
				pagingOptions: {
					...defaultPagingOptions,
					...options
				},
				totalPages: 0,
				totalElements: 0
			};
		});

		// Inizializza sempre il contesto 'default'
		if (!initialItems['default']) {
			initialItems['default'] = {
				pagingOptions: { ...defaultPagingOptions },
				totalPages: 0,
				totalElements: 0
			};
		}

		return initialItems;
	});

	// Traccia i contesti che devono essere creati
	const pendingContexts = useRef<Set<string>>(new Set());

	// Ottieni un contesto specifico, creandolo se non esiste
	const getContextItem = useCallback(
		(contextId: string): PagingContextItem => {
			// Se il contesto non esiste, segnala che deve essere creato
			// ma restituisce un oggetto temporaneo per evitare errori durante il rendering
			if (!pagingItems[contextId]) {
				pendingContexts.current.add(contextId);
				return {
					pagingOptions: { ...defaultPagingOptions },
					totalPages: 0,
					totalElements: 0
				};
			}

			return pagingItems[contextId];
		},
		[pagingItems]
	);

	// Effetto per creare i contesti pendenti
	useEffect(() => {
		if (pendingContexts.current.size > 0) {
			setPagingItems((prev) => {
				const updated = { ...prev };

				pendingContexts.current.forEach((contextId) => {
					if (!updated[contextId]) {
						updated[contextId] = {
							pagingOptions: { ...defaultPagingOptions },
							totalPages: 0,
							totalElements: 0
						};
					}
				});

				// Svuota la lista dei contesti pendenti
				pendingContexts.current.clear();

				return updated;
			});
		}
	}, [pagingItems]);

	// Aggiorna le opzioni di paginazione per un contesto specifico
	const setPagingOptions = useCallback(
		(contextId: string, options: Partial<PagingOptions>) => {
			setPagingItems((prev) => {
				const currentItem = prev[contextId] || {
					pagingOptions: { ...defaultPagingOptions },
					totalPages: 0,
					totalElements: 0
				};

				return {
					...prev,
					[contextId]: {
						...currentItem,
						pagingOptions: {
							...currentItem.pagingOptions,
							...options
						}
					}
				};
			});
		},
		[]
	);

	// Imposta le informazioni di paginazione totali per un contesto specifico dopo la chiamata API
	const setTotalInfo = useCallback(
		(contextId: string, totalPages: number, totalElements: number) => {
			setPagingItems((prev) => {
				const currentItem = prev[contextId] || {
					pagingOptions: { ...defaultPagingOptions },
					totalPages: 0,
					totalElements: 0
				};

				return {
					...prev,
					[contextId]: {
						...currentItem,
						totalPages,
						totalElements
					}
				};
			});
		},
		[]
	);

	// Reset alle opzioni di default per un contesto specifico
	const resetPaging = useCallback((contextId: string) => {
		setPagingItems((prev) => {
			const currentItem = prev[contextId] || {
				pagingOptions: { ...defaultPagingOptions },
				totalPages: 0,
				totalElements: 0
			};

			return {
				...prev,
				[contextId]: {
					...currentItem,
					pagingOptions: { ...defaultPagingOptions }
				}
			};
		});
	}, []);

	return (
		<PagingContext.Provider
			value={{
				pagingItems,
				setPagingOptions,
				setTotalInfo,
				resetPaging,
				getContextItem
			}}
		>
			{children}
		</PagingContext.Provider>
	);
};

export default PagingProvider;
